import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import { HelpCenterSearchResult } from '../models/helpCenterSearchResult';
import { highlightWords } from '../functions/highlightWords';

type HelpPanelSearchResultsProps = {
  /** Sökningen som träffarna hör till; orden i den blir feta. */
  query: string;
  results: HelpCenterSearchResult[];
  /** Inget svar ännu för någon sökning. Ett nytt sökord visar de förra träffarna tills nya kommer. */
  loading: boolean;
  failed: boolean;
  onOpen: (slug: string) => void;
};

/** Sökträffarna i hjälppanelen: titel och utdrag med sökorden i fetstil. */
export const HelpPanelSearchResults = (props: HelpPanelSearchResultsProps) => {
  const { query, results, loading, failed, onOpen } = props;
  const { t } = useTranslation('help');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  // role=status: skärmläsaren läser upp antalet och tomt/fel när listan byts medan man skriver (fokus står i fältet).
  if (failed) {
    return (
      <Typography role='status' color='text.secondary'>
        {t('help:loadFailed')}
      </Typography>
    );
  }
  if (results.length === 0) {
    return (
      <Typography role='status' color='text.secondary'>
        {t('help:searchNoHits', { query })}
      </Typography>
    );
  }

  return (
    <>
      <Typography role='status' variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
        {t('help:panelSearchHits', { count: results.length })}
      </Typography>
      <List disablePadding>
        {results.map((result) => (
          <ListItemButton key={result.slug} onClick={() => onOpen(result.slug)} sx={{ borderRadius: 1, display: 'block' }}>
            {/* Normal vikt på titeln, så att sökorden syns i fetstil. */}
            <Typography color='primary'>{highlightWords(result.title, query)}</Typography>
            {result.snippet && (
              <Typography
                variant='body2'
                color='text.secondary'
                // Högst två rader, så att listan går att överblicka i den smala panelen.
                sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {highlightWords(result.snippet, query)}
              </Typography>
            )}
          </ListItemButton>
        ))}
      </List>
    </>
  );
};
